import { useState } from 'react';
import { Box, Tabs, Tab, Typography, Grid, Paper, Stack } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTeacher } from '@/hooks/useTeacher';
import { Loader } from '@/components/feedback/Loader';
import { PATHS } from '@/router/paths';
import { TeacherProfileHeader } from './components/TeacherProfileHeader';
import { TeacherStatisticsCard } from './components/TeacherStatisticsCard';
import { BookOpen, Users, Calendar, Award } from 'lucide-react';
import useTeacherStore from '@/store/teacherStore';
import { useSchool } from '@/hooks/useSchool';
import toast from 'react-hot-toast';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/constants/permissions';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value || '-'}
      </Typography>
    </Box>
  );
}

export function TeacherProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { schoolId } = useSchool();
  const { teacher, loading } = useTeacher(id);
  const deleteTeacher = useTeacherStore(s => s.deleteTeacher);
  
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const { can } = usePermission();
  const canDelete = can(PERMISSIONS.TEACHER_DELETE);

  if (loading) return <Loader />;
  if (!teacher) return <Typography>Teacher not found</Typography>;

  const handleDelete = async () => {
    try {
      await deleteTeacher(schoolId, teacher.id);
      toast.success('Teacher deactivated successfully');
      navigate(PATHS.TEACHERS);
    } catch (err) {
      toast.error('Failed to deactivate teacher');
    }
  };

  return (
    <Box>
      <TeacherProfileHeader 
        teacher={teacher} 
        onEdit={() => navigate(`${PATHS.TEACHERS}/${id}/edit`)}
        onDelete={canDelete ? () => setDeleteDialogOpen(true) : undefined}
      />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TeacherStatisticsCard 
            title="Classes Assigned" 
            value="3" 
            icon={BookOpen} 
            color="primary" 
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TeacherStatisticsCard 
            title="Total Students" 
            value="85" 
            icon={Users} 
            color="info" 
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TeacherStatisticsCard 
            title="Attendance" 
            value="98%" 
            subtitle="This month"
            icon={Calendar} 
            color="success" 
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TeacherStatisticsCard 
            title="Experience" 
            value={teacher.experience || 'N/A'} 
            icon={Award} 
            color="warning" 
          />
        </Grid>
      </Grid>

      <Paper sx={{ borderRadius: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Overview" />
          <Tab label="Professional" />
          <Tab label="Documents" />
          {/* Placeholders for future modules */}
          <Tab label="Attendance" />
          <Tab label="Leave" />
          <Tab label="Performance" />
        </Tabs>

        <Box px={3}>
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2}>Personal Information</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}><DetailRow label="Gender" value={teacher.gender} /></Grid>
                  <Grid size={{ xs: 6 }}><DetailRow label="Date of Birth" value={teacher.dateOfBirth} /></Grid>
                  <Grid size={{ xs: 6 }}><DetailRow label="Blood Group" value={teacher.bloodGroup} /></Grid>
                  <Grid size={{ xs: 6 }}><DetailRow label="Aadhaar" value={teacher.aadhaar} /></Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2}>Contact Information</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}><DetailRow label="Phone" value={teacher.phone} /></Grid>
                  <Grid size={{ xs: 6 }}><DetailRow label="Email" value={teacher.email} /></Grid>
                  <Grid size={{ xs: 12 }}><DetailRow label="Address" value={teacher.address} /></Grid>
                  <Grid size={{ xs: 12 }}><DetailRow label="Emergency Contact" value={teacher.emergencyContact} /></Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2}>Employment Details</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}><DetailRow label="Employee ID" value={teacher.employeeId} /></Grid>
                  <Grid size={{ xs: 6 }}><DetailRow label="Joining Date" value={teacher.joiningDate} /></Grid>
                  <Grid size={{ xs: 6 }}><DetailRow label="Employment Type" value={teacher.employmentType} /></Grid>
                  <Grid size={{ xs: 6 }}><DetailRow label="PAN" value={teacher.pan} /></Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2}>Qualifications</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}><DetailRow label="Degrees & Certifications" value={teacher.qualification} /></Grid>
                  <Grid size={{ xs: 12 }}><DetailRow label="Previous Experience" value={teacher.experience} /></Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Stack spacing={2}>
              {teacher.resume && (
                <DetailRow label="Resume / CV" value={teacher.resume} />
              )}
              {teacher.appointmentLetter && (
                <DetailRow label="Appointment Letter" value={teacher.appointmentLetter} />
              )}
              {teacher.certificates?.length > 0 && (
                <DetailRow label="Certificates" value={teacher.certificates.join(', ')} />
              )}
              {!teacher.resume && !teacher.appointmentLetter && (!teacher.certificates || teacher.certificates.length === 0) && (
                <Typography color="text.secondary">No documents uploaded.</Typography>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Typography color="text.secondary" align="center" py={4}>
              Attendance data will be integrated in future phases.
            </Typography>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Typography color="text.secondary" align="center" py={4}>
              Leave management will be integrated in future phases.
            </Typography>
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <Typography color="text.secondary" align="center" py={4}>
              Performance metrics will be integrated in future phases.
            </Typography>
          </TabPanel>
        </Box>
      </Paper>

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Deactivate Teacher"
        message="Are you sure you want to deactivate this teacher? They will no longer be able to access the system."
        confirmText="Deactivate"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
